import React, { useEffect, useRef, useState } from "react";

import qualitySelector from "@silvermine/videojs-quality-selector";
import classNames from "classnames";
import { useIntl } from "gatsby-plugin-react-intl";
import PropTypes from "prop-types";
import videojs from "video.js";

import useEventListener from "../../hooks/haiku/useEventListener";
import useWindowDimensions from "../../hooks/useWindowDimensions";

import "./video.scss";

const Video = ({
  asBackground = false,
  options = {},
  onReady = () => {},
  vttUrl = undefined,
  sources,
  transcriptRef = undefined,
}) => {
  const intl = useIntl();
  const videoRef = useRef(null);
  const playerRef = useRef(null);

  const { width } = useWindowDimensions();
  const [quality, setQuality] = useState();

  useEffect(() => {
    if (width === 0 || (!sources.xs && !sources.md && !sources.xl)) {
      return;
    }

    let res = sources.xs ? "SD" : undefined;

    if (width >= 1200 && sources.xl) {
      res = "FHD";
    } else if (width >= 768 && sources.md) {
      res = "HD";
    }

    setQuality(res);
  }, [width]);

  useEffect(() => {
    if (!playerRef.current || (!sources.xs && !sources.md && !sources.xl)) return;

    const tmpSources = [
      sources.xs && { label: "SD", selected: quality === "SD", ...sources.xs },
      sources.md && { label: "HD", selected: quality === "HD", ...sources.md },
      sources.xl && { label: "Full HD", selected: quality === "FHD", ...sources.xl },
    ].filter((item) => item);

    playerRef.current.src(tmpSources);
  }, [quality]);

  useEffect(() => {
    // Make sure Video.js player is only initialized once
    const {
      current,
    } = playerRef;

    if (!current) {
      videojs.addLanguage(process.env.GATSBY_LANGUAGES, {
        "Exit Fullscreen": intl.formatMessage({ id: "video.exit_fullscreen" }),
        Fullscreen: intl.formatMessage({ id: "video.fullscreen" }),
        Mute: intl.formatMessage({ id: "video.mute" }),
        Pause: intl.formatMessage({ id: "video.pause" }),
        Play: intl.formatMessage({ id: "video.play" }),
        "Play Video": intl.formatMessage({ id: "video.play" }),
        Replay: intl.formatMessage({ id: "video.replay" }),
        Subtitles: intl.formatMessage({ id: "video.subtitles" }),
        Unmute: intl.formatMessage({ id: "video.unmute" }),
        "subtitles off": intl.formatMessage({ id: "video.subtitles_off" }),
      });
      // The Video.js player needs to be _inside_ the component el for React 18 Strict Mode.
      const videoElement = document.createElement("video-js");

      videoRef.current.appendChild(videoElement);

      qualitySelector(videojs);
      const playerOptions = {
        ...options,
        language: process.env.GATSBY_LANGUAGES,
      };

      if (vttUrl) {
        playerOptions.tracks = [
          {
            default: options.transcripting,
            kind: "subtitles",
            label: intl.formatMessage({ id: "video.subtitles_on" }),
            src: vttUrl,
            srclang: process.env.GATSBY_LANGUAGES,
          },
        ];
      }

      const player = videojs(videoElement, playerOptions, () => {
        onReady(player);
      });

      const Button = videojs.getComponent("Button");
      const button = new Button(player, {
        className: "transcript-button",
        clickHandler() {
          if (transcriptRef.current) {
            transcriptRef.current.show();
          }
        },
      });
      player.controlBar.addChild(
        button,
        {},
        player.controlBar.children().findIndex(
          (child) => child === player.controlBar.fullscreenToggle,
        ),
      );
      player.controlBar.addChild("QualitySelector");
      playerRef.current = player;
    }

    if (current) {
      if (current.textTracks().length > 0) {
        const textTrack = current.textTracks()[0];

        if (options.transcripting) {
          textTrack.mode = "showing";
        } else {
          textTrack.mode = "disabled";
        }
      }
    }
  }, [options, videoRef]);

  useEffect(() => {
    const player = playerRef.current;

    return () => {
      if (player && !player.isDisposed()) {
        player.dispose();
        playerRef.current = null;
      }
    };
  }, [playerRef]);

  useEventListener(
    "keypress",
    (event) => {
      if (!asBackground) {
        const {
          current,
        } = playerRef;

        if (!current) {
          return;
        }

        switch (event.code) {
          case "Space":
          case "KeyK":
            if (current.paused()) {
              current.play();
            } else {
              current.pause();
            }
            break;
          case "KeyF":
            if (current.isFullscreen()) {
              current.exitFullscreen();
            } else {
              current.requestFullscreen();
            }
            break;
          case "KeyM":
            current.muted(!current.muted());
            break;
          case "KeyC":
            if (current.textTracks().length > 0) {
              const textTrack = current.textTracks()[0];
              textTrack.mode = textTrack.mode === "showing" ? "disabled" : "showing";
            }
            break;
          default:
            break;
        }
      }
    },
  );

  useEventListener(
    "keydown",
    (event) => {
      if (!asBackground) {
        const {
          current,
        } = playerRef;

        if (!current) {
          return;
        }

        switch (event.code) {
          case "ArrowUp":
            current.volume(Math.floor((current.volume() + 0.05) * 100) / 100);
            break;
          case "ArrowDown":
            current.volume(Math.floor((current.volume() - 0.05) * 100) / 100);
            break;
          case "ArrowLeft":
            current.currentTime(current.currentTime() - 5);
            break;
          case "ArrowRight":
            current.currentTime(current.currentTime() + 5);
            break;
          default:
            break;
        }
      }
    },
  );

  return (
    <div
      data-vjs-player
      className={classNames(
        "video-player",
        { "video-player--background": asBackground },
      )}
      ref={videoRef}
    />
  );
};

Video.propTypes = {
  asBackground: PropTypes.bool,
  onReady: PropTypes.func,
  options: PropTypes.oneOfType([PropTypes.object]),
  sources: PropTypes.shape({
    md: PropTypes.shape({
      src: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    }),
    xl: PropTypes.shape({
      src: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    }),
    xs: PropTypes.shape({
      src: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    }),
  }).isRequired,
  transcriptRef: PropTypes.node,
  vttUrl: PropTypes.string,
};

export default Video;
